import React, { useCallback, useEffect } from 'react'
import styled, { CSSObject } from '@emotion/styled'
import {
  Map,
  APIProvider,
  useMap,
  AdvancedMarker
} from '@vis.gl/react-google-maps'
import { BikeIcon, CarIcon, MotorbikeIcon, VanIcon } from 'shop/assets/vehicles'
import { IoMdHome as HomeIcon, IoMdWalk as WalkIcon } from 'react-icons/io'

import env from 'shop/env'
import { Delivery, FullAddress } from 'shop/types/cart'
import { useImages } from 'shop/hooks'
import { ImageTagType } from 'shop/types'
import { StoreIcon, StoreMarker } from '../Maps/commonStyles'

type LatLng = {
  lat: number
  lng: number
}

type TrackMapProps = {
  delivery?: Delivery
  destination?: FullAddress
  origin?: FullAddress
  config: any
  originLogoUrl?: string // logo url for orgin marker
}

const trackingAllowedStatuses = ['delivering', 'almost_delivering']

const iconStyles = {
  position: 'relative' as any,
  zIndex: 1,
  height: '100%',
  width: '100%',
  fill: '#000'
}

const strokeIconStyles = { ...iconStyles, fill: 'none', stroke: '#000' }

const vehicleNames = {
  motorbikexl: 'motorbike',
  motorcycle: 'motorbike',
  motorcycle_cargo: 'motorbike',
  motorbike_cargo: 'motorbike',
  bicycle: 'bike',
  bicycle_cargo: 'bike',
  walking: 'walk'
}

const TrackMap = ({
  config,
  destination,
  origin,
  delivery,
  originLogoUrl
}: TrackMapProps) => {
  return (
    <APIProvider apiKey={env.MAPS_API_KEY}>
      <Map
        mapId={'4fe2e51066302762793a9398'}
        id='track-map'
        defaultCenter={env.DEFAULT_MAP_CENTER}
        defaultZoom={12}
        fullscreenControl={false}
        streetViewControl={false}
        mapTypeControl={false}
        zoomControlOptions={{
          position: google.maps.ControlPosition.RIGHT_CENTER
        }}
      >
        <MapContent
          config={config}
          destination={destination}
          origin={origin}
          delivery={delivery}
          originLogoUrl={originLogoUrl}
        />
      </Map>
    </APIProvider>
  )
}

// Child component with access to map context
const MapContent = ({
  config,
  destination,
  origin,
  delivery,
  originLogoUrl
}: TrackMapProps) => {
  const map = useMap('track-map')
  const { getImage } = useImages(
    [{ imageUrl: originLogoUrl || '' }],
    ImageTagType.Standard
  )

  // Pins drawing logic
  const drawPin = useCallback(
    (key: string, lat: number, lng: number, icon: React.ReactNode) => {
      return (
        <AdvancedMarker key={key} position={{ lat, lng }}>
          <PinContainer>
            <PinBackground />
            <PinContent>{icon}</PinContent>
          </PinContainer>
        </AdvancedMarker>
      )
    },
    []
  )

  const drawCourierPin = useCallback(() => {
    if (
      !delivery?.driver ||
      !trackingAllowedStatuses.includes(delivery.status || '')
    ) {
      return
    }

    const { contactNumber, transportType, coordinates } = delivery.driver

    const { lat, lng } = coordinates || {}

    if ((!lat && lat !== 0) || (!lng && lng !== 0) || !transportType) {
      return
    }

    const vehicle = vehicleNames[transportType.toLowerCase()]
      ? vehicleNames[transportType.toLowerCase()]
      : transportType.toLowerCase()

    const vehicleIcons = {
      walk: <WalkIcon style={iconStyles} />,
      bike: <BikeIcon style={iconStyles} />,
      car: <CarIcon style={iconStyles} />,
      motorbike: <MotorbikeIcon style={iconStyles} />,
      van: <VanIcon style={strokeIconStyles} />
    }

    return drawPin(contactNumber, lat, lng, vehicleIcons[vehicle])
  }, [drawPin, delivery])

  // Map recenter logic
  const fitBounds = useCallback(
    (point1: LatLng, point2: LatLng) => {
      if (!map || !google?.maps) return

      const bounds = new google.maps.LatLngBounds()
      bounds.extend(new google.maps.LatLng(point1.lat, point1.lng))
      bounds.extend(new google.maps.LatLng(point2.lat, point2.lng))

      map.fitBounds(bounds)
    },
    [map]
  )

  const drawDestinationPin = useCallback(() => {
    if (!destination?.coordinates) {
      return
    }

    const { coordinates, zip } = destination
    const { lat, lng } = coordinates

    if ((!lat && lat !== 0) || (!lng && lng !== 0)) {
      return
    }

    return drawPin(
      `destination-${zip}`,
      lat,
      lng,
      <HomeIcon style={iconStyles} />
    )
  }, [destination, drawPin])

  const drawOriginPin = useCallback(() => {
    if (!origin?.coordinates) {
      return
    }

    const { coordinates, zip } = origin
    const { lat, lng } = coordinates

    if ((!lat && lat !== 0) || (!lng && lng !== 0)) {
      return
    }

    return (
      <AdvancedMarker key={`${zip}-origin`} position={{ lat, lng }}>
        <StoreMarker isClickable={false}>
          <StoreIcon src={getImage(50, 50)} alt={origin.lineOne || 'store'} />
        </StoreMarker>
      </AdvancedMarker>
    )
  }, [origin, config, getImage])

  useEffect(() => {
    if (!map) {
      return
    }

    const originCoordinates = origin?.coordinates
    const destinationCoordinates = destination?.coordinates
    if (!originCoordinates || !destinationCoordinates) {
      return
    }

    fitBounds(originCoordinates, destinationCoordinates)
  }, [map, origin, destination, fitBounds])

  return (
    <>
      {drawOriginPin()}
      {drawCourierPin()}
      {drawDestinationPin()}
    </>
  )
}

const PinContainer = styled.div(
  (): CSSObject => ({
    padding: '6px',
    width: '32px',
    height: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    transform: 'translate(-50%, -50%)'
  })
)

const PinBackground = styled.div(
  (): CSSObject => ({
    width: '100%',
    height: '100%',
    background: 'lightblue',
    opacity: 0.6,
    borderRadius: '50%',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  })
)

const PinContent = styled.div(
  (): CSSObject => ({
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  })
)

export default TrackMap
